import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { KeyValueDTO, PrincipalService } from '@ms/core';
import { Observable, first, of, switchMap, tap } from 'rxjs';
import { GradeLevel } from '../grade-levels/model/grade-level.model';
import { logRequest } from '../shared/http-utils';
import { GradeLevelStore } from '../store/gradelevel.store';

@Injectable({
    providedIn: 'root',
})
export class GradeLevelsService {
    baseUrl = '/school-service/api/grade-levels';
    private http = inject(HttpClient);
    private principalService = inject(PrincipalService);
    private store = inject(GradeLevelStore);

    getAll(): Observable<GradeLevel[]> {
        if (!this.store.loaded()) {
            return this.http.get<GradeLevel[]>(this.baseUrl).pipe(tap(result => this.store.set(result)));
        } else {
            return of(this.store.findAll()).pipe(first());
        }
    }

    getAllActive(): Observable<GradeLevel[]> {
        if (!this.store.loaded()) {
            return this.getAll().pipe(switchMap(() => of(this.store.findAllActive()).pipe(first())));
        } else {
            return of(this.store.findAllActive()).pipe(first());
        }
    }

    getAllBySchoolId(): Observable<GradeLevel[]> {
        if (!this.store.loaded()) {
            return this.getAll().pipe(switchMap(() => of(this.store.findAll()).pipe(first())));
        } else {
            return of(this.store.findAll()).pipe(first());
        }
    }

    getAllByTenantId(tenantId: string): Observable<GradeLevel[]> {
        return this.http.get<GradeLevel[]>(this.baseUrl + `/tenant/${tenantId}`);
    }

    getById(id: string): Observable<GradeLevel> {
        if (!this.store.loaded()) {
            return this.getAll().pipe(switchMap(() => of(this.store.get(id)).pipe(first())));
        } else {
            return of(this.store.get(id)).pipe(first());
        }
    }

    create(gradeLevel: GradeLevel): Observable<HttpResponse<GradeLevel>> {
        return this.http
            .post<GradeLevel>(this.baseUrl, gradeLevel, {
                observe: 'response',
                headers: logRequest(),
            })
            .pipe(tap(result => this.store.add(result.body)));
    }

    update(gradeLevel: GradeLevel): Observable<HttpResponse<GradeLevel>> {
        return this.http
            .put<GradeLevel>(this.baseUrl, gradeLevel, {
                observe: 'response',
                headers: logRequest(),
            })
            .pipe(tap(result => this.store.update(result.body)));
    }

    delete(id: string): Observable<HttpResponse<any>> {
        return this.http
            .delete(`${this.baseUrl}/${id}`, {
                observe: 'response',
                headers: logRequest(),
            })
            .pipe(tap(() => this.store.remove(id)));
    }

    getAllActiveForSelectionList(): Observable<KeyValueDTO<GradeLevel>[]> {
        const schoolId = this.principalService?.authenticatedUser?.activeSchool?.id;
        if (!this.store.loaded()) {
            return this.getAll().pipe(
                switchMap(() =>
                    of(
                        this.store
                            .findBySchoolId(schoolId)
                            .map(
                                i =>
                                    new KeyValueDTO<GradeLevel>(
                                        i.id,
                                        i.name,
                                        i,
                                        i.shortName,
                                        null,
                                        null,
                                        null,
                                        i.order,
                                        i.active
                                    )
                            )
                    ).pipe(first())
                )
            );
        } else {
            return of(
                this.store
                    .findBySchoolId(schoolId)
                    .map(
                        i =>
                            new KeyValueDTO<GradeLevel>(
                                i.id,
                                i.name,
                                i,
                                i.shortName,
                                null,
                                null,
                                null,
                                i.order,
                                i.active
                            )
                    )
            ).pipe(first());
        }
    }

    setActive(id: string, isActive: boolean) {
        return this.http
            .patch(`${this.baseUrl}/${id}/active/${isActive}`, { headers: logRequest() })
            .pipe(tap(() => this.store.setActive(id, isActive)));
    }
}
